import React from 'react'
import styled from 'styled-components'

const Container = styled.div`
  min-width: 160px;
  max-width: 240px;

  @media (max-width: 768px) {
    min-width: 180px;
    max-width: 250px;
  }

  @media (max-width: 480px) {
    min-width: 160px;
    max-width: 200px;
  }
`

const PermitNumber = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #00ade6;
  margin-bottom: 6px;
`

const PermitTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  margin: 0 0 6px 0;
  color: #1a1a1a;
  line-height: 1.3;
`

const PermitDetails = styled.div`
  font-size: 10px;
  color: #6b6b6b;
  line-height: 1.4;

  div {
    margin-bottom: 3px;
  }
`

const Badge = styled.span<{ $statusColor?: string }>`
  display: inline-block;
  padding: 2px 5px;
  background: ${({ $statusColor }) =>
    $statusColor ? `${$statusColor}20` : 'rgba(0, 173, 230, 0.1)'};
  color: ${({ $statusColor }) => $statusColor || '#00ade6'};
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  margin: 2px;
`

const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 3px;
  margin: -2px;
`

const getStatusColor = (status: string): string => {
  const s = status?.toLowerCase() || ''
  if (s.includes('accepté') || s.includes('accepted') || s.includes('approved'))
    return '#10b981'
  if (s.includes('refusé') || s.includes('rejected') || s.includes('refused'))
    return '#ef4444'
  if (
    s.includes('instruction') ||
    s.includes('pending') ||
    s.includes('attente')
  )
    return '#f59e0b'
  if (s.includes('terminé') || s.includes('completed')) return '#6b7280'
  return '#00ade6'
}

const getCategoryLabel = (category: string): string => {
  const map: Record<string, string> = {
    'construction nouvelle': 'New Construction',
    development: 'Development',
    renovation: 'Renovation',
    rénovation: 'Renovation',
    transformation: 'Transformation',
    surélévation: 'Elevation',
    elevation: 'Elevation',
    démolition: 'Demolition',
    demolition: 'Demolition',
    régularisation: 'Regularisation',
    regularisation: 'Regularisation',
  }
  return map[category?.toLowerCase()] || category
}

const getStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    'en instruction': 'Under Review',
    instruction: 'Under Review',
    enregistrement: 'Registered',
    accepté: 'Approved',
    accepted: 'Approved',
    approved: 'Approved',
    'en attente': 'Pending',
    pending: 'Pending',
    refusé: 'Rejected',
    refused: 'Rejected',
    rejected: 'Rejected',
    terminé: 'Completed',
    completed: 'Completed',
  }
  return map[status?.toLowerCase()] || status
}

const formatDate = (dateStr: string): string | null => {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return null
  }
}

interface BuildingPermitMapTooltipProps {
  properties: Record<string, any>
}

export const BuildingPermitMapTooltip: React.FC<
  BuildingPermitMapTooltipProps
> = ({ properties }) => {
  const {
    id,
    primary_official_id,
    fao_id,
    description,
    city_name,
    postal_codes_names,
    postal_code,
    regbl_classification_general,
    latest_status_db,
    inquiry_start_date,
  } = properties || {}

  const permitNumber = primary_official_id || fao_id || `ID-${id}`
  const category = regbl_classification_general
  const status = latest_status_db

  // Build location string
  const postalCodeStr =
    postal_code ||
    (typeof postal_codes_names === 'string'
      ? postal_codes_names.split(' ')?.[0]
      : Array.isArray(postal_codes_names)
        ? postal_codes_names[0]?.split(' ')?.[0]
        : '')
  const location =
    postalCodeStr && city_name
      ? `${postalCodeStr} ${city_name}`
      : city_name || ''

  return (
    <Container>
      <PermitNumber>Permit #{permitNumber}</PermitNumber>

      {description && (
        <PermitTitle>
          {description.length > 60
            ? `${description.substring(0, 60)}...`
            : description}
        </PermitTitle>
      )}

      <PermitDetails>
        {location && <div>{location}</div>}
        {inquiry_start_date && (
          <div>Filed: {formatDate(inquiry_start_date)}</div>
        )}
      </PermitDetails>

      <BadgeContainer>
        {status && (
          <Badge $statusColor={getStatusColor(status)}>
            {getStatusLabel(status)}
          </Badge>
        )}
        {category && <Badge>{getCategoryLabel(category)}</Badge>}
      </BadgeContainer>
    </Container>
  )
}
